import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Loader2, LogIn, Stethoscope } from 'lucide-react';
import api from '../../utils/api';

export default function DoctorLogin() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.email.trim() || !form.password) {
      setError('Email and password are required');
      return;
    }

    setLoading(true);
    try {
      // PRD1.9 Phase 3: Use unified login endpoint
      const { data } = await api.post('/auth/unified/login', form);
      if (data.success) {
        localStorage.setItem('treatx_unified_user', JSON.stringify(data.user));
        navigate('/doctor/profile');
      } else {
        setError(data.error || data.detail || 'Login failed');
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.response?.data?.error;
      setError(typeof detail === 'string' ? detail : 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="doctor-login-page">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-12 h-12 rounded-full bg-teal-50 flex items-center justify-center mb-3">
            <Stethoscope className="h-6 w-6 text-teal-600" />
          </div>
          <CardTitle className="text-xl font-bold text-slate-900">Doctor Login</CardTitle>
          <p className="text-sm text-slate-500 mt-1">Access your doctor profile dashboard</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder="doctor@example.com"
                data-testid="doctor-login-email"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={form.password}
                onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
                placeholder="Your password"
                data-testid="doctor-login-password"
              />
            </div>

            {error && (
              <p className="text-sm text-red-500" data-testid="doctor-login-error">{error}</p>
            )}

            <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700" disabled={loading} data-testid="doctor-login-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <LogIn className="h-4 w-4 mr-2" />}
              Log In
            </Button>

            <p className="text-center text-xs text-slate-400 mt-3">
              Don't have an account? <Link to="/doctor/signup" className="text-teal-600 hover:underline">Sign up</Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
